import { SettingsIcon, SidebarShowIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { cn } from "cn";
import { useState } from "react";

import { UNTITLED_CONVERSATION } from "../chat/conversations.ts";
import { ConversationUsage } from "../usage/ConversationUsage.tsx";
import { useWorkspace } from "./AppShell.tsx";
import { useSession } from "./session.tsx";
import { SettingsDialog } from "./SettingsDialog.tsx";
import { Sidebar, type SidebarPanel } from "./Sidebar.tsx";
import { TopBar } from "./TopBar.tsx";
import { pathOf } from "./workspace.ts";

/**
 * El armazón conversacional (§9.2).
 *
 * Es el ÚNICO layout con historial y redactor: la columna de la izquierda con
 * los chats y la memoria, y al centro la conversación.
 *
 * Ocupa la ventana entera y NO centra nada: la barra lateral se pega al borde
 * izquierdo y el contenido se queda con el resto.
 */
export function ChatShell() {
  const { chat, onConversationDeleted } = useWorkspace();
  const navigate = useNavigate();
  const { canSeeCosts } = useSession();
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  /** El panel de la izquierda: los chats, salvo que el usuario elija otro. */
  const [chosenPanel, setChosenPanel] = useState<SidebarPanel | null>(null);
  const panel = chosenPanel ?? "chats";

  const goToChat = () => {
    void navigate({ to: "/", search: (previous) => previous });
  };

  return (
    <div className="flex h-dvh overflow-hidden">
      {!sidebarHidden && (
        <Sidebar
          currentId={chat.conversationId}
          panel={panel}
          onPanel={setChosenPanel}
          onOpen={(id) => {
            chat.open(id);
            goToChat();
          }}
          onDeleted={onConversationDeleted}
          // "Conversación nueva" vuelve a significar solo eso: empezar de
          // cero. Ya no es la única forma de ver el conmutador de modos.
          onNew={() => {
            chat.startNew();
            goToChat();
          }}
          onHide={() => {
            setSidebarHidden(true);
          }}
        />
      )}

      {/* `min-w-0` y `min-h-0`: un hijo flex vale por defecto "no me
          encojas por debajo de mi contenido", y sin esto una tabla ancha
          estira el armazón y la página entera se mueve en horizontal. */}
      <main className="flex min-h-0 min-w-0 flex-1 flex-col">
        <TopBar
          mode="chat"
          onMode={(next) => {
            void navigate({
              to: pathOf(next),
              search: (previous) => previous,
            });
          }}
          context={
            <>
              {sidebarHidden && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setSidebarHidden(false);
                  }}
                  aria-label="Mostrar el panel"
                >
                  <SidebarShowIcon />
                </Button>
              )}

              {chat.conversationId !== undefined && (
                /* Mientras el backend le pone nombre, la cabecera lo dice
                     con las mismas palabras que el historial y en el mismo
                     sitio: solo cambia el color, así que cuando llegue el
                     título de verdad no salta nada. */
                <h1
                  className={cn(
                    "truncate text-base font-semibold",
                    chat.title === null && "font-normal text-muted-foreground",
                  )}
                  title={chat.title ?? UNTITLED_CONVERSATION}
                >
                  {chat.title ?? UNTITLED_CONVERSATION}
                </h1>
              )}

              {/* Lo que va costando: solo para el superadmin. */}
              {canSeeCosts && chat.usage !== undefined && (
                <ConversationUsage usage={chat.usage} />
              )}
            </>
          }
          actions={
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setSettingsOpen(true);
              }}
              aria-label="Ajustes"
            >
              <SettingsIcon />
            </Button>
          }
        />

        <Outlet />
      </main>

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}
